import { useState, useEffect } from "react";
import { mergeObjects } from "../../../../shared/helper";
import { useFormik } from "formik";
import { pluginFormValidation } from "../../validations";
import {
  useGetPluginQuery,
  useCreatePluginMutation,
  useUpdatePluginMutation,
  useGetAppsQuery,
  useGetContainersQuery,
  useCreatePluginFeaturesMutation,
  useUpdatePluginFeaturesMutation,
} from "../../services";
import { useForm } from "../../../../hooks";
import * as Components from "../../../../components";
import { FormContainer } from "../../Shared";
import { getLocalizedString } from "../../../../shared/translation";
import { da } from "date-fns/locale";
import { importRemote } from "../../../Plugins/importRemote";
import { extractPluginFeatures } from "./pluginFeatureUtils";
import { FMDPStorage } from "../../../../shared/helper";

export const PluginForm = ({ idToEdit = null, onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFileLoaded, setIsFileLoaded] = useState(false);
  const [mpcontainers, setMPContainers] = useState([]);

  const [tcontainers, setTcontainers] = useState([]);

  const [pluginFeatures, setPluginFeatures] = useState([]);
  const [featureExtractionError, setFeatureExtractionError] = useState("");

  const [appMap, setAppMap] = useState({});
  const [mpMap, setMMap] = useState({});
  const [tMap, setTMap] = useState({});

  const [initialValues, setInitialValues] = useState({
    bundle: null,
    name: "",
    path: "",
    description: "",
    author: "",
    version: "",
    status: "under review",
    scope: "",
    module: "",
    permission: [],
    mp_containers: [],
    tracker_containers: [],
    is_enabled: false,
    loadedBundle: "",
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: pluginFormValidation(!!idToEdit),
    onSubmit: (values) => handleFormSubmit(values),
  });

  const { create: createPlugin, update: updatePlugin } = useForm({
    createMutation: useCreatePluginMutation,
    updateMutation: useUpdatePluginMutation,
    closeForm: onClose,
    setError: formik.setErrors,
  });

  const [createPluginFeatures] = useCreatePluginFeaturesMutation();
  const [updatePluginFeatures] = useUpdatePluginFeaturesMutation();

  // Fetch the data if edit form
  const { data: selectedPlugin } = useGetPluginQuery({ id: idToEdit }, { skip: !idToEdit });

  // Get existing plugin features from plugin API response
  const existingPluginFeatures = selectedPlugin?.plugin_features?.[0] || null;

  // Populate pluginFeatures from existing API data when editing
  useEffect(() => {
    if (idToEdit && existingPluginFeatures) {
      const config = existingPluginFeatures?.features_json?.config;
      if (Array.isArray(config) && config.length > 0) {
        setPluginFeatures(config);
      }
    }
  }, [idToEdit, existingPluginFeatures]);

  // Get list of apps subscribed by client
  const { data: clientApp = { data: [] }, isSuccess: clientAppSuccess } = useGetAppsQuery();

  //Get Containers query

  const { data: newContainers } = useGetContainersQuery({ page: 0, per_page: 500 });

  // Modify Containers data if new containers are present
  useEffect(() => {
    if (newContainers?.data?.length) {
      const mpData = [];
      const tData = [];

      newContainers.data.forEach((container) => {
        if (container?.client_app_names?.includes("Management Portal")) {
          mpData.push({ id: container?.id, name: container.name });
        }
        if (container?.client_app_names?.includes("Tracking")) {
          tData.push({ id: container?.id, name: container.name });
        }
      });

      setMPContainers(mpData);
      setTcontainers(tData);
    }
  }, [newContainers]);

  useEffect(() => {
    if (idToEdit && selectedPlugin?.bundle_url?.url) {
      fetch(selectedPlugin?.bundle_url?.url)
        .then((response) => response.blob())
        .then((blob) => {
          const fileName = selectedPlugin?.bundle_url?.url.split("/").pop();
          const file = new File([blob], fileName, { type: blob.type });
          formik.setFieldValue("bundle", file);
          formik.setFieldValue("loadedBundle", fileName); // Ensure loadedBundle is updated
          setIsFileLoaded(true); // Mark file as loaded

          // Extract features from the loaded bundle
          const scopeName = fileName.split(".")[0];
          const moduleName = scopeName.charAt(0).toUpperCase() + scopeName.slice(1);
          const tempUrl = URL.createObjectURL(file);
          importRemote({ url: tempUrl, scope: scopeName, module: moduleName, isHosted: false })
            .then((pluginModule) => {
              const features = extractPluginFeatures(pluginModule);
              setPluginFeatures(features);
              setFeatureExtractionError("");
            })
            .catch((err) => {
              console.error("Error extracting features from bundle:", err);
              setFeatureExtractionError("Failed to extract features from plugin bundle.");
              setPluginFeatures([]);
            })
            .finally(() => {
              URL.revokeObjectURL(tempUrl);
            });
        })
        .catch((error) => {
          console.error("Error fetching the bundle file:", error);
          formik.setFieldValue("loadedBundle", ""); // Handle error case
          setIsFileLoaded(true); // Proceed even if there's an error
        });
    }
  }, [selectedPlugin, idToEdit]);

  useEffect(() => {
    if (idToEdit && selectedPlugin) {
      const updatedPlugin = {
        ...selectedPlugin,
        permission: selectedPlugin.permission.map((s) => parseInt(s, 10)),
        mp_containers: selectedPlugin.mp_containers?.map((s) => parseInt(s, 10)),
        tracker_containers: selectedPlugin.tracker_containers?.map((s) => parseInt(s, 10)),
      };

      setInitialValues(mergeObjects(initialValues, updatedPlugin));

      const tempMPData = selectedPlugin?.mp_containers?.map((mp_container) => ({
        id: 1,
        mp_containers: mp_container,
        tracker_containers: "",
      }));

      const tempTData = selectedPlugin?.tracker_containers?.map((tracker_container) => ({
        id: 2,
        mp_containers: "",
        tracker_containers: tracker_container,
      }));

      const combinedData = [...tempMPData, ...tempTData];
      setReorderedMappingData(combinedData);
    }
  }, [selectedPlugin, idToEdit]);

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    let fObj = new FormData();

    Object.keys(formData).forEach((key) => {
      if (key === "bundle") {
        fObj.append(key, formData[key]);
      } else {
        if (typeof formData[key] === "object") {
          fObj.append(key, JSON.stringify(formData[key]));
        } else {
          fObj.append(key, formData[key]);
        }
      }
    });

    // Remove the delete icon key if it exists
    if (fObj.has("icon")) {
      fObj.delete("icon");
    }

    // Features are now sent via separate plugin_features API
    // No longer appended to the plugin FormData

    const appFeatures = FMDPStorage.get("app_features");

    try {
      let pluginResult;
      if (idToEdit) {
        pluginResult = await updatePlugin({ formData: fObj, id: idToEdit });
      } else {
        pluginResult = await createPlugin({ formData: fObj });
      }

      // After plugin is saved, send features via plugin_features API
      if (pluginFeatures.length > 0 && !pluginResult?.error) {
        const pluginId = idToEdit || pluginResult?.data?.id;
        // app_feature_id comes from Plugins permission in app_features (localStorage)
        // create action → Plugins.create id, update action → Plugins.update id
        const appFeatureId = idToEdit
          ? appFeatures?.Plugins?.update
          : appFeatures?.Plugins?.create;

        const featuresConfig = pluginFeatures.map((f) => ({
          key: f.key,
          label: f.label,
          allowed_permissions: f.allowed_permissions || ["read"],
        }));

        if (idToEdit && existingPluginFeatures) {
          // PUT /plugin_features/:pluginFeaturesId — use plugin_features ID, not plugin ID
          await updatePluginFeatures({
            id: existingPluginFeatures.id,
            formData: {
              app_feature_id: appFeatureId,
              plugin_id: pluginId,
              features_json: {
                enabled: formData.is_enabled,
                config: featuresConfig,
              },
            },
          });
        } else {
          // POST /plugin_features — create new plugin features
          await createPluginFeatures({
            formData: {
              app_feature_id: appFeatureId,
              plugin_id: pluginId,
              features_json: {
                config: featuresConfig,
              },
            },
          });
        }
      }
    } catch (err) {
      console.error("Error saving plugin features:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const applications = [
    { id: 1, name: "Management Portal" },
    { id: 2, name: "Tracking" },
  ];

  useEffect(() => {
    if (applications || mpcontainers || tcontainers) {
      const newAppMap = {}; // Temporary object to store key-value pairs
      applications.forEach((app) => {
        newAppMap[app.id] = app.name;
      });

      setAppMap(newAppMap);

      let newMpMap = {}; // Temporary object to store key-value pairs
      mpcontainers.forEach((mp) => {
        newMpMap[mp.id] = mp.name;
      });

      setMMap(newMpMap);

      let newTMap = {}; // Temporary object to store key-value pairs
      tcontainers.forEach((t) => {
        newTMap[t.id] = t.name;
      });

      setTMap(newTMap);
    }
  }, [mpcontainers, tcontainers]);

  useEffect(() => {
    setDisplayNameMap({ id: appMap, mp_containers: mpMap, tracker_containers: tMap });
  }, [appMap, mpMap, tMap]);

  const [mappingValues, setmappingValues] = useState({
    id: "",
    mp_containers: "",
    tracker_containers: "",
  });

  const formikMapping = useFormik({
    initialValues: mappingValues,
    enableReinitialize: true,
  });

  const [reorderedMappingData, setReorderedMappingData] = useState([]);
  const [displayNameMap, setDisplayNameMap] = useState({});
  const mapColumnNames = ["id", "mp_containers", "tracker_containers"];

  const [validationError, setValidationError] = useState("");

  const handleSubmitContainer = (values) => {
    // Make sure we're working with the current values, not stale ones
    const currentValues = { ...formikMapping.values };

    // Check if all required fields have values
    if (
      currentValues.id &&
      ((currentValues.id === 1 && currentValues.mp_containers) ||
        (currentValues.id === 2 && currentValues.tracker_containers))
    ) {
      // Update permission array with unique application IDs
      formik.setFieldValue(
        "permission",
        Array.from(new Set([...formik.values.permission, currentValues.id]))
      );

      // Update containers based on selected application
      if (currentValues.id === 1) {
        // For Management Portal
        formik.setFieldValue("mp_containers", [
          ...formik.values.mp_containers,
          currentValues.mp_containers,
        ]);
      } else if (currentValues.id === 2) {
        // For Tracker
        formik.setFieldValue("tracker_containers", [
          ...formik.values.tracker_containers,
          currentValues.tracker_containers,
        ]);
      }

      // Clear any validation error
      setValidationError("");

      // Reset the form values after successful submission
      formikMapping.resetForm();
      formikMapping.setValues({
        id: "",
        mp_containers: "",
        tracker_containers: "",
      });

      // Return true to allow adding the row
      return true;
    }

    // Set validation error message
    setValidationError("Please select both Application and Container");

    // Return false to prevent adding the row if validation fails
    return false;
  };

  // Add this validator function
  const validateContainerMapping = (item, existingItems) => {
    // Check if application (id) is selected
    if (!item.id) {
      setValidationError("Please select an Application");
      return true; // validation failed
    }

    // Check if appropriate container is selected based on application type
    if (item.id === 1 && !item.mp_containers) {
      setValidationError("Please select a Container for Management Portal");
      return true; // validation failed
    }

    if (item.id === 2 && !item.tracker_containers) {
      setValidationError("Please select a Container for Tracking");
      return true; // validation failed
    }

    // Check for duplicates
    const isDuplicate = existingItems.some((existing) => {
      if (item.id === 1 && existing.id === 1) {
        return existing.mp_containers === item.mp_containers;
      }
      if (item.id === 2 && existing.id === 2) {
        return existing.tracker_containers === item.tracker_containers;
      }
      return false;
    });

    if (isDuplicate) {
      setValidationError("This container mapping already exists");
      return true; // validation failed
    }

    // Clear validation error if all checks pass
    setValidationError("");
    return false; // validation passed
  };

  const handleOnCancel = () => {
    // Reset the form values when canceling
    formikMapping.resetForm();
    formikMapping.setValues({
      id: "",
      mp_containers: "",
      tracker_containers: "",
    });
    setValidationError("");
  };

  const [headers, setHeaders] = useState([
    {
      label: "Applications",
      key: "permission",
      error: "",
      dataType: "",
    },
    {
      label: "Container",
      key: "mp_containers || tracker_containers",
      error: "",
      dataType: "",
    },
    {
      className: "action",
    },
  ]);

  const [searchTerm, setSearchTerm] = useState("");

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleChange = (name, value) => {
    formikMapping.setFieldValue(name, JSON?.parse(value)?.id);
    if (name === "id") {
      formikMapping.setFieldValue("mp_containers", "");
      formikMapping.setFieldValue("tracker_containers", "");
    }
  };

  useEffect(() => {
    if (formik.values.bundle) {
      const scopeName = formik.values.bundle.name.split(".")[0];
      const moduleName = scopeName.charAt(0).toUpperCase() + scopeName.slice(1);
      formik.setFieldValue("scope", scopeName);
      formik.setFieldValue("module", moduleName);

      // Extract features from uploaded bundle (create mode only)
      if (!idToEdit) {
        const tempUrl = URL.createObjectURL(formik.values.bundle);
        importRemote({ url: tempUrl, scope: scopeName, module: moduleName, isHosted: false })
          .then((pluginModule) => {
            const features = extractPluginFeatures(pluginModule);
            setPluginFeatures(features);
            setFeatureExtractionError("");
          })
          .catch((err) => {
            console.error("Error extracting features from bundle:", err);
            setFeatureExtractionError("Failed to extract features from plugin bundle.");
            setPluginFeatures([]);
          })
          .finally(() => {
            URL.revokeObjectURL(tempUrl);
          });
      }
    }
    if (formik.values.name) {
      // Convert name to kebab case for path
      const kebabCasePath = formik.values.name.replace(/\s+/g, "-").toLowerCase();
      formik.setFieldValue("path", kebabCasePath);
    }
  }, [formik.values]);

  return (
    <FormContainer
      resourceName={"Plugin"}
      closeForm={() => onClose(false)}
      handleFormSubmit={formik.handleSubmit}
      idToEdit={idToEdit}
      isLoading={isLoading}
    >
      {!idToEdit && (
        <Components.QIFileUploader
          label="JavaScript bundle"
          placeholder={formik.values.bundle ? "" : "Only .js files"}
          onChange={(files) => formik.setFieldValue("bundle", files[0])}
          error={formik.touched.bundle && formik.errors.bundle}
        />
      )}
      {idToEdit && (
        <Components.QIInput
          label={getLocalizedString("javascript-bundle", "JavaScript bundle")}
          readOnly
          {...formik.getFieldProps("loadedBundle")}
          error={formik.touched.loadedBundle && formik.errors.loadedBundle}
          warningMessage={!isFileLoaded && "Loading Bundle"}
          className="grey"
        />
      )}
      <Components.QIInput
        label={"Plugin Name"}
        {...formik.getFieldProps("name")}
        error={formik.touched.name && formik.errors.name}
      />
      <Components.QIInput
        label={"URL Path"}
        disabled
        {...formik.getFieldProps("path")}
        error={formik.touched.path && formik.errors.path}
      />
      <Components.QIInput
        label={"Scope"}
        disabled
        {...formik.getFieldProps("scope")}
        error={formik.touched.scope && formik.errors.scope}
      />
      <Components.QIInput
        label={"Module"}
        disabled
        {...formik.getFieldProps("module")}
        error={formik.touched.module && formik.errors.module}
      />
      <Components.QIInput
        label={"Description"}
        {...formik.getFieldProps("description")}
        error={formik.touched.description && formik.errors.description}
      />
      <Components.QIInput
        label={"Author Name"}
        {...formik.getFieldProps("author")}
        // error={formik.touched.author && formik.errors.author}
      />
      <Components.QIInput
        label={"Version"}
        {...formik.getFieldProps("version")}
        //error={formik.touched.version && formik.errors.version}
      />
      <Components.QITableCustom
        handleSubmit={handleSubmitContainer}
        headers={headers}
        objectItem={formikMapping.values}
        validationRequired={true}
        validator={validateContainerMapping}
        handleOnCancel={handleOnCancel}
        initialValues={reorderedMappingData}
        displayNameMap={displayNameMap}
        mapColumnNames={mapColumnNames}
        error={validationError || (formik.touched.permission && formik.errors.permission)}
        setValidationError={setValidationError}
        setSelectedVariableId={() => {}}
      >
        <div className="flex items-center gap-48">
          <div className="qi-list-view_column">
            <select className="qi-select_text" onChange={(e) => handleChange("id", e.target.value)}>
              <>
                <optgroup label="">
                  <input type="text" value={searchTerm} onChange={handleSearchChange} />
                </optgroup>
                <option value="select">select</option>
                {clientApp?.data
                  ?.filter((data) => data?.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((data, index) => (
                    <option value={JSON.stringify(data)} key={index}>
                      {data?.name}
                    </option>
                  ))}
              </>
            </select>
            <div className="text-red-600 small-error">{headers[0]?.error}</div>
          </div>
          <div className="qi-list-view_column">
            <select
              className="qi-select_text"
              onChange={(e) =>
                handleChange(
                  formikMapping.values.id === 1 ? "mp_containers" : "tracker_containers",
                  e.target.value
                )
              }
            >
              <>
                <optgroup label="">
                  <input type="text" value={searchTerm} onChange={handleSearchChange} />
                </optgroup>
                <option value="select">select</option>
                {formikMapping.values.id === 1
                  ? mpcontainers
                      .filter((data) => data?.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map((data, index) => (
                        <option value={JSON.stringify(data)} key={index}>
                          {data?.name}
                        </option>
                      ))
                  : tcontainers
                      .filter((data) => data?.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map((data, index) => (
                        <option value={JSON.stringify(data)} key={index}>
                          {data?.name}
                        </option>
                      ))}
              </>
            </select>
            <div className="text-red-600 small-error">{headers[1]?.error}</div>
          </div>
        </div>
      </Components.QITableCustom>
      <Components.QISwitch
        label={getLocalizedString("Enabled", "Enabled")}
        {...formik.getFieldProps("is_enabled")}
        onChange={() => formik.setFieldValue("is_enabled", !formik.values.is_enabled)}
      />

      {featureExtractionError && (
        <div className="text-red-600 text-sm" style={{ padding: "4px 0" }}>
          {featureExtractionError}
        </div>
      )}

      {pluginFeatures.length > 0 && (
        <div style={{ padding: "8px 0" }}>
          <label className="qi-input_label">Plugin Features</label>
          <ul style={{ listStyle: "none", padding: 0, margin: "4px 0 0 0" }}>
            {pluginFeatures.map((feature) => (
              <li
                key={feature.key}
                style={{
                  padding: "4px 8px",
                  fontSize: "13px",
                  borderBottom: "1px solid #eee",
                }}
              >
                <span style={{ fontWeight: 500 }}>{feature.label}</span>
                <span style={{ color: "#888", marginLeft: "8px" }}>({feature.key})</span>
                {feature.allowed_permissions && (
                  <span style={{ color: "#666", marginLeft: "8px", fontSize: "12px" }}>
                    [{feature.allowed_permissions.join(", ")}]
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </FormContainer>
  );
};
